import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Badge,
  Modal,
  Form,
  Alert,
  Row,
  Col,
  ProgressBar,
  Dropdown,
} from "react-bootstrap";
import Loader from "../../components/common/Loader";
import api from "../../services/api";
import {
  FileText,
  CheckCircle,
  XCircle,
  Eye,
  Search,
  Filter,
  Clock,
  Calendar,
  Download,
  FileEarmarkText,
  FileEarmarkPdf,
  FileEarmarkImage,
  FileEarmarkZip,
  FileEarmarkWord,
  FileEarmarkExcel,
  FileEarmarkPerson,
  Bank,
  Award,
  FileEarmarkCheck,
  SortDown,
} from "react-bootstrap-icons";

const DocumentReview = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await api.get("/documents/admin/all");

      if (response.data.success) {
        const docs = response.data.data || [];
        console.log("Documents fetched:", docs);

        // Group documents by appointmentId
        const grouped = docs.reduce((acc, doc) => {
          if (!acc[doc.appointmentId]) acc[doc.appointmentId] = [];
          acc[doc.appointmentId].push(doc);
          return acc;
        }, {});
        setDocuments(grouped);
      } else {
        setError(response.data.error || "Failed to load documents");
      }
    } catch (err) {
      console.error("Error fetching documents:", err);
      setError(
        err.response?.status === 401
          ? "Session expired. Please login again."
          : "Failed to load documents"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (fileName, appointmentId, verified) => {
    try {
      await api.post("/documents/admin/verify", {
        fileName,
        appointmentId,
        verified,
        verificationNotes,
      });

      // Update local state
      setDocuments((prev) => {
        const updated = { ...prev };
        updated[appointmentId] = updated[appointmentId].map((doc) =>
          doc.fileName === fileName
            ? { ...doc, verified, verificationNotes }
            : doc
        );
        return updated;
      });

      setShowModal(false);
      setVerificationNotes("");
      setError(null);
    } catch (err) {
      console.error("Error verifying document:", err);
      setError("Failed to verify document");
    }
  };

  const openVerificationModal = (doc) => {
    setSelectedDocument(doc);
    setShowModal(true);
  };

  const getDocumentIcon = (type) => {
    switch (type) {
      case "passport":
        return <FileEarmarkPerson className="text-primary" size={20} />;
      case "bank_statement":
        return <Bank className="text-success" size={20} />;
      case "academic_records":
        return <Award className="text-info" size={20} />;
      case "sop":
        return <FileEarmarkText className="text-warning" size={20} />;
      case "cv":
        return <FileEarmarkPerson className="text-secondary" size={20} />;
      case "pdf":
        return <FileEarmarkPdf className="text-danger" size={20} />;
      case "image":
        return <FileEarmarkImage className="text-info" size={20} />;
      default:
        return <FileText className="text-secondary" size={20} />;
    }
  };

  const getDocumentTypeBadge = (type) => {
    const colors = {
      passport: "primary",
      bank_statement: "success",
      academic_records: "info",
      sop: "warning",
      cv: "secondary",
      fsf: "dark",
      pdf: "danger",
      image: "info",
      document: "warning",
      other: "light",
    };
    return (
      <Badge
        bg={colors[type] || "secondary"}
        className="px-3 py-2 rounded-pill"
      >
        {getDocumentIcon(type)} <span className="ms-1">{type}</span>
      </Badge>
    );
  };

  const getVerificationBadge = (verified) => {
    return verified ? (
      <Badge bg="success" className="px-3 py-2 rounded-pill">
        <CheckCircle className="me-1" /> Verified
      </Badge>
    ) : (
      <Badge bg="warning" className="px-3 py-2 rounded-pill">
        <Clock className="me-1" /> Pending
      </Badge>
    );
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileExtensionIcon = (fileName) => {
    const ext = fileName?.split(".").pop()?.toLowerCase();
    switch (ext) {
      case "pdf":
        return <FileEarmarkPdf className="text-danger" />;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <FileEarmarkImage className="text-info" />;
      case "doc":
      case "docx":
        return <FileEarmarkWord className="text-primary" />;
      case "xls":
      case "xlsx":
        return <FileEarmarkExcel className="text-success" />;
      case "zip":
      case "rar":
        return <FileEarmarkZip className="text-warning" />;
      default:
        return <FileText className="text-secondary" />;
    }
  };

  // Calculate document statistics
  const getDocumentStats = () => {
    let allDocs = [];
    Object.values(documents).forEach(
      (docs) => (allDocs = [...allDocs, ...docs])
    );

    return {
      total: allDocs.length,
      verified: allDocs.filter((doc) => doc.verified).length,
      pending: allDocs.filter((doc) => !doc.verified).length,
      types: {
        passport: allDocs.filter((doc) => doc.type === "passport").length,
        bank_statement: allDocs.filter((doc) => doc.type === "bank_statement")
          .length,
        academic_records: allDocs.filter(
          (doc) => doc.type === "academic_records"
        ).length,
        sop: allDocs.filter((doc) => doc.type === "sop").length,
        cv: allDocs.filter((doc) => doc.type === "cv").length,
      },
    };
  };

  // Filter documents
  const getFilteredDocuments = () => {
    let filtered = {};

    Object.entries(documents).forEach(([appointmentId, docs]) => {
      const filteredDocs = docs.filter((doc) => {
        // Search filter
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          if (
            !(
              doc.appointmentId?.toLowerCase().includes(searchLower) ||
              doc.fileName?.toLowerCase().includes(searchLower) ||
              doc.type?.toLowerCase().includes(searchLower)
            )
          )
            return false;
        }

        // Status filter
        if (statusFilter !== "all") {
          if (statusFilter === "verified" && !doc.verified) return false;
          if (statusFilter === "pending" && doc.verified) return false;
        }

        // Type filter
        if (typeFilter !== "all" && doc.type !== typeFilter) return false;

        return true;
      });

      if (filteredDocs.length > 0) {
        filtered[appointmentId] = filteredDocs;
      }
    });

    return filtered;
  };

  const filteredDocuments = getFilteredDocuments();
  const stats = getDocumentStats();

  if (loading) return <Loader />;

  const pageStyle = {
    minHeight: "100vh",
    padding: "20px",
  };

  const cardStyle = {
    borderRadius: "15px",
    border: "none",
    boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
    background: "rgba(255,255,255,0.95)",
  };

  const statCardStyle = {
    borderRadius: "12px",
    border: "none",
    color: "white",
    padding: "20px",
  };

  return (
    <div style={pageStyle}>
      <div className="container-fluid">
        {/* Header */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1 fw-bold text-gradient">
                <FileText className="me-3" size={32} />
                Document Review
              </h2>
              <p className="text-muted mb-0">
                Review and verify student submission documents
              </p>
            </div>
            <Button
              variant="primary"
              className="d-flex align-items-center px-4 py-2"
              onClick={fetchDocuments}
              style={{
                borderRadius: "10px",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                border: "none",
              }}
            >
              <FileEarmarkCheck className="me-2" size={20} />
              Refresh
            </Button>
          </div>

          {/* Stats Cards */}
          <Row className="mb-4 g-3">
            <Col md={3}>
              <Card
                style={{
                  ...statCardStyle,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                }}
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <FileText size={24} />
                      </div>
                    </div>
                    <div>
                      <h2 className="mb-0 fw-bold">{stats.total}</h2>
                      <p className="mb-0 opacity-75">Total Documents</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card
                style={{
                  ...statCardStyle,
                  background:
                    "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                }}
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CheckCircle size={24} />
                      </div>
                    </div>
                    <div>
                      <h2 className="mb-0 fw-bold">{stats.verified}</h2>
                      <p className="mb-0 opacity-75">Verified</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card
                style={{
                  ...statCardStyle,
                  background:
                    "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
                }}
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Clock size={24} />
                      </div>
                    </div>
                    <div>
                      <h2 className="mb-0 fw-bold">{stats.pending}</h2>
                      <p className="mb-0 opacity-75">Pending</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card
                style={{
                  ...statCardStyle,
                  background:
                    "linear-gradient(135deg, #17a2b8 0%, #6f42c1 100%)",
                }}
              >
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div
                        style={{
                          width: "50px",
                          height: "50px",
                          background: "rgba(255,255,255,0.2)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <FileEarmarkPerson size={24} />
                      </div>
                    </div>
                    <div>
                      <h2 className="mb-0 fw-bold">
                        {stats.types.passport + stats.types.cv}
                      </h2>
                      <p className="mb-0 opacity-75">Personal Docs</p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Search and Filter Bar */}
          <Card className="mb-4" style={cardStyle}>
            <Card.Body className="p-4">
              <Row className="align-items-center g-3">
                <Col md={5}>
                  <div
                    className="input-group"
                    style={{ borderRadius: "10px", overflow: "hidden" }}
                  >
                    <span className="input-group-text bg-white border-end-0">
                      <Search size={20} className="text-muted" />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 ps-0"
                      placeholder="Search by Appointment ID, filename, or type..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ border: "none", fontSize: "16px" }}
                    />
                  </div>
                </Col>
                <Col md={3}>
                  <div className="d-flex align-items-center">
                    <Filter className="me-2 text-muted" size={20} />
                    <select
                      className="form-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      style={{ borderRadius: "10px" }}
                    >
                      <option value="all">All Status</option>
                      <option value="verified">Verified Only</option>
                      <option value="pending">Pending Only</option>
                    </select>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="d-flex align-items-center">
                    <FileText className="me-2 text-muted" size={20} />
                    <select
                      className="form-select"
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      style={{ borderRadius: "10px" }}
                    >
                      <option value="all">All Types</option>
                      <option value="passport">Passport</option>
                      <option value="bank_statement">Bank Statement</option>
                      <option value="academic_records">Academic Records</option>
                      <option value="sop">SOP</option>
                      <option value="cv">CV</option>
                    </select>
                  </div>
                </Col>
                <Col md={1} className="text-end">
                  <Button
                    variant="outline-secondary"
                    style={{ borderRadius: "10px" }}
                    onClick={() => {
                      setSearchTerm("");
                      setStatusFilter("all");
                      setTypeFilter("all");
                    }}
                  >
                    Clear
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Documents Table */}
          <Card style={cardStyle}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 className="fw-bold mb-1">
                    <FileText className="me-2" />
                    Document List
                  </h5>
                  <p className="text-muted mb-0">
                    Showing {Object.values(filteredDocuments).flat().length} of{" "}
                    {stats.total} documents
                  </p>
                </div>
              </div>

              {error && (
                <Alert
                  variant="danger"
                  onClose={() => setError(null)}
                  dismissible
                  className="border-0 shadow-sm mb-4"
                >
                  <div className="d-flex align-items-center">
                    <XCircle className="me-2" size={20} />
                    <span>{error}</span>
                  </div>
                </Alert>
              )}

              {Object.keys(filteredDocuments).length > 0 ? (
                <div
                  className="table-responsive"
                  style={{ borderRadius: "10px", overflow: "hidden" }}
                >
                  <Table hover className="mb-0">
                    <thead
                      style={{
                        background:
                          "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        color: "white",
                      }}
                    >
                      <tr>
                        <th className="ps-4 py-3">
                          <div className="d-flex align-items-center">
                            <SortDown className="me-2" />
                            Appointment ID
                          </div>
                        </th>
                        <th className="py-3">Document</th>
                        <th className="py-3">Type</th>
                        <th className="py-3">Size</th>
                        <th className="py-3">Uploaded</th>
                        <th className="py-3">Status</th>
                        <th className="py-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(filteredDocuments).map(
                        ([appointmentId, docs]) => (
                          <React.Fragment key={appointmentId}>
                            {docs.map((doc, idx) => (
                              <tr
                                key={`${appointmentId}-${idx}`}
                                style={{
                                  transition: "all 0.3s ease",
                                  borderBottom: "1px solid #f1f1f1",
                                }}
                              >
                                <td className="ps-4">
                                  <div className="d-flex align-items-center">
                                    <div
                                      style={{
                                        width: "40px",
                                        height: "40px",
                                        background: "#f8f9fa",
                                        borderRadius: "10px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        marginRight: "12px",
                                      }}
                                    >
                                      <Calendar
                                        size={18}
                                        className="text-primary"
                                      />
                                    </div>
                                    <div>
                                      <div className="fw-bold">
                                        {doc.appointmentId}
                                      </div>
                                      <div className="small text-muted">
                                        Uploaded:{" "}
                                        {doc.uploadedAt
                                          ? new Date(
                                              doc.uploadedAt
                                            ).toLocaleDateString()
                                          : "N/A"}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div
                                      className="me-3"
                                      style={{ fontSize: "24px" }}
                                    >
                                      {getFileExtensionIcon(doc.fileName)}
                                    </div>
                                    <div>
                                      <div className="fw-medium">
                                        {doc.fileName}
                                      </div>
                                      <div className="small text-muted">
                                        File: .
                                        {doc.fileName
                                          ?.split(".")
                                          .pop()
                                          ?.toUpperCase()}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td>{getDocumentTypeBadge(doc.type)}</td>
                                <td>
                                  <div className="fw-medium">
                                    {formatFileSize(doc.fileSize)}
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <Clock
                                      className="me-2 text-muted"
                                      size={16}
                                    />
                                    <div>
                                      <div className="fw-medium">
                                        {doc.uploadedAt
                                          ? new Date(
                                              doc.uploadedAt
                                            ).toLocaleDateString()
                                          : "N/A"}
                                      </div>
                                      <div className="small text-muted">
                                        {doc.uploadedAt
                                          ? new Date(
                                              doc.uploadedAt
                                            ).toLocaleTimeString()
                                          : ""}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    {getVerificationBadge(doc.verified)}
                                  </div>
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    {doc.filePath && (
                                      <>
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          className="d-flex align-items-center"
                                          onClick={() =>
                                            window.open(
                                              `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:5000'}/${doc.filePath}`,
                                              "_blank"
                                            )
                                          }
                                          style={{ borderRadius: "8px" }}
                                        >
                                          <Eye className="me-1" /> View
                                        </Button>
                                        <Button
                                          variant={
                                            doc.verified
                                              ? "outline-secondary"
                                              : "outline-success"
                                          }
                                          size="sm"
                                          className="d-flex align-items-center"
                                          onClick={() =>
                                            openVerificationModal(doc)
                                          }
                                          style={{ borderRadius: "8px" }}
                                        >
                                          {doc.verified
                                            ? "Re-verify"
                                            : "Verify"}
                                        </Button>
                                      </>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        )
                      )}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-5">
                  <FileText size={64} className="text-muted mb-3" />
                  <h5 className="text-muted">No documents found</h5>
                  <p className="text-muted mb-0">
                    Try adjusting your search or filters
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Verification Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="fw-bold">
            <div className="d-flex align-items-center">
              <div
                style={{
                  width: "50px",
                  height: "50px",
                  background:
                    "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  marginRight: "15px",
                }}
              >
                <FileEarmarkCheck size={24} />
              </div>
              <div>
                Document Verification
                {selectedDocument && (
                  <div className="small text-muted mt-1">
                    File: {selectedDocument.fileName}
                  </div>
                )}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          {selectedDocument && (
            <div className="mb-4">
              <div
                className="p-3 mb-3"
                style={{
                  background: "#f8f9fa",
                  borderRadius: "10px",
                  borderLeft: "4px solid #4361ee",
                }}
              >
                <div className="row g-2">
                  <div className="col-6">
                    <small className="text-muted d-block">Appointment ID</small>
                    <div className="fw-medium">
                      {selectedDocument.appointmentId}
                    </div>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-block">Document Type</small>
                    <div>{getDocumentTypeBadge(selectedDocument.type)}</div>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-block">File Size</small>
                    <div className="fw-medium">
                      {formatFileSize(selectedDocument.fileSize)}
                    </div>
                  </div>
                  <div className="col-6">
                    <small className="text-muted d-block">Current Status</small>
                    <div>{getVerificationBadge(selectedDocument.verified)}</div>
                  </div>
                </div>
              </div>

              <Form.Group className="mb-4">
                <Form.Label className="fw-medium">
                  <FileText className="me-1" />
                  Verification Notes
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={verificationNotes}
                  onChange={(e) => setVerificationNotes(e.target.value)}
                  placeholder="Add any notes or comments about this document..."
                  className="py-2"
                  style={{ borderRadius: "8px" }}
                />
              </Form.Group>

              <div className="d-flex justify-content-between">
                <Button
                  variant="outline-primary"
                  className="px-4"
                  style={{ borderRadius: "10px" }}
                  onClick={() =>
                    window.open(
                      `${process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:5000'}/${selectedDocument.filePath}`,
                      "_blank"
                    )
                  }
                >
                  <Eye className="me-1" /> Preview Document
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button
            variant="light"
            onClick={() => setShowModal(false)}
            className="px-4"
            style={{ borderRadius: "10px" }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() =>
              handleVerify(
                selectedDocument?.fileName,
                selectedDocument?.appointmentId,
                false
              )
            }
            className="px-4"
            style={{ borderRadius: "10px" }}
          >
            <XCircle className="me-1" /> Reject
          </Button>
          <Button
            variant="success"
            onClick={() =>
              handleVerify(
                selectedDocument?.fileName,
                selectedDocument?.appointmentId,
                true
              )
            }
            className="px-4"
            style={{ borderRadius: "10px" }}
          >
            <CheckCircle className="me-1" /> Approve
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DocumentReview;
